import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Dumbbell, Eye, EyeOff, Loader2, Bug } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useToast } from '@/hooks/use-toast';
import { reportError, setPageArea } from '@/lib/sentry';
import * as Sentry from '@sentry/react';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [isRecovering, setIsRecovering] = useState(false);

  // Login State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { login, sendResetPasswordEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [searchParams] = useSearchParams();
  const isDebugMode = searchParams.get('debug') === 'true' || searchParams.has('debug');

  useEffect(() => {
    setPageArea("auth");
  }, []);

  if (isDebugMode) {
    console.log('[SENTRY DEBUG] Modo Debug: Activo');
  }

  const handleTestSentry = () => {
    toast({
      title: "Enviando alerta...",
      description: "Se está disparando un error de prueba a Sentry.",
    });

    try {
      throw new Error(`Prueba de Sentry @ ${new Date().toLocaleTimeString()} - Conexión Telegram`);
    } catch (error) {
      const eventId = Sentry.captureException(error, {
        tags: { area: 'auth', test: 'true' }
      });
      console.log("Evento Sentry enviado. ID:", eventId);
      toast({
        title: "ID de Evento",
        description: `Enviado con ID: ${eventId}`,
      });
    }
  };

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const success = await login(email, password);

      if (success) {
        toast({
          title: '¡Bienvenido!',
          description: 'Sesión iniciada correctamente',
        });
        navigate(from, { replace: true });
      } else {
        // Here login doesn't "throw" on bad credentials, it returns success=false
        // but it's still useful to track failed attempts if we want.
        // For now, let's only track actual errors if the login function throws.
        toast({
          title: 'Error de autenticación',
          description: 'Email o contraseña incorrectos',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      reportError(error, { operation: 'login_auth', area: 'auth' });
      toast({
        title: 'Error de conexión',
        description: error.message || 'Ocurrió un error al intentar iniciar sesión',
        variant: 'destructive',
      });
    }

    setIsLoading(false);
  };

  const handleRecoverPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { success, error } = await sendResetPasswordEmail(email);

      if (success) {
        toast({
          title: '¡Email enviado!',
          description: 'Revisa tu bandeja de entrada para restablecer tu contraseña.',
        });
        setIsRecovering(false);
      } else {
        toast({
          title: 'Error',
          description: error || 'No se pudo enviar el correo de recuperación',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      reportError(error, { operation: 'recover_password', area: 'auth' });
      toast({
        title: 'Error de servidor',
        description: 'No se pudo procesar la recuperación en este momento.',
        variant: 'destructive',
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left Panel - Branding (Visible on Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900 border-r border-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="/hero-vibrant.png"
            alt="Fitness Energy"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        </div>

        <div className="relative z-10 flex flex-col justify-center items-start w-full p-16 text-white text-left">
          <div className="flex items-center gap-3 mb-10 group cursor-pointer" onClick={() => navigate('/')}>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-600 shadow-xl shadow-orange-600/30 transition-transform group-hover:scale-110">
              <Dumbbell className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-black tracking-tight">GymPro SV</h1>
          </div>

          <h2 className="text-6xl font-black leading-[0.9] tracking-tighter mb-8 max-w-md">
            IMPULSA TU <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
              RESULTADO
            </span>
          </h2>

          <p className="text-xl text-slate-300 font-medium max-w-sm">
            La plataforma definitiva para la gestión de gimnasios en El Salvador.
          </p>
        </div>

        <div className="absolute bottom-8 left-16 text-slate-500 text-xs font-bold uppercase tracking-widest">
          © {new Date().getFullYear()} GymPro SV • Ágil y Profesional
        </div>
      </div>

      {/* Right Panel - Login/Register Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 lg:p-12">
        <div className="absolute top-6 right-6">
          <ThemeToggle />
        </div>

        <div className="w-full max-w-md animate-in slide-in-from-right duration-500">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center gap-4 mb-10" onClick={() => navigate('/')}>
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-600 shadow-xl shadow-orange-600/20">
              <Dumbbell className="h-10 w-10 text-white" />
            </div>
            <span className="text-3xl font-black text-slate-900 tracking-tight">GymPro SV</span>
          </div>

          <Card className="border-none shadow-2xl shadow-slate-200/50 rounded-3xl overflow-hidden bg-white">
            <div className="h-2 bg-gradient-to-r from-orange-600 to-amber-500" />
            <CardHeader className="space-y-1 pb-4 pt-8 text-center lg:text-left lg:px-10">
              <CardTitle className="text-3xl font-black text-slate-900 leading-tight">
                {isRecovering ? 'Restablecer' : (isLogin ? '¡Bienvenido!' : 'Crear Cuenta')}
              </CardTitle>
              <CardDescription className="text-slate-500 font-medium">
                {isRecovering
                  ? 'Te enviaremos un enlace de recuperación.'
                  : (isLogin ? 'Ingresa tus credenciales para continuar.' : 'Empieza tu prueba gratuita hoy mismo.')
                }
              </CardDescription>
            </CardHeader>

            <CardContent className="lg:px-10 pb-10">
              <form onSubmit={isRecovering ? handleRecoverPassword : handleLogin} className="space-y-5">

                {!isRecovering && (
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs font-bold uppercase text-slate-400 ml-1">Correo Electrónico</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 bg-slate-50 border-slate-100 focus:bg-white focus:border-orange-500/30 transition-all rounded-xl text-slate-900 font-semibold"
                    />
                  </div>
                )}

                {isRecovering && (
                  <div className="space-y-2">
                    <Label htmlFor="recover-email" className="text-xs font-bold uppercase text-slate-400 ml-1">Email de recuperación</Label>
                    <Input
                      id="recover-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 bg-slate-50 border-slate-100 focus:bg-white focus:border-orange-500/30 transition-all rounded-xl"
                    />
                  </div>
                )}

                {!isRecovering && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-xs font-bold uppercase text-slate-400 ml-1">Contraseña</Label>
                      {isLogin && (
                        <Button
                          type="button"
                          variant="link"
                          className="px-0 h-auto text-xs font-bold text-orange-600 hover:text-orange-700 hover:no-underline"
                          onClick={() => setIsRecovering(true)}
                        >
                          ¿Olvidaste tu contraseña?
                        </Button>
                      )}
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="h-12 pr-12 bg-slate-50 border-slate-100 focus:bg-white focus:border-orange-500/30 transition-all rounded-xl text-slate-900 font-semibold"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-12 w-12 hover:bg-transparent text-slate-400"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-14 text-lg font-bold bg-slate-900 hover:bg-slate-800 text-white rounded-2xl transition-all shadow-lg hover:shadow-xl mt-4"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      PROCESANDO...
                    </>
                  ) : (
                    isRecovering ? 'ENVIAR ENLACE' : (isLogin ? 'INICIAR SESIÓN' : 'EMPEZAR PRUEBA')
                  )}
                </Button>

                <div className="text-center pt-2">
                  {!isRecovering && (
                    <p className="text-sm font-medium text-slate-500">
                      {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => navigate('/register-gym')}
                        className="text-sm font-bold text-orange-600 hover:text-orange-700 px-1.5"
                      >
                        {isLogin ? 'Regístrate' : 'Inicia sesión'}
                      </Button>
                    </p>
                  )}
                  {isRecovering && (
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setIsRecovering(false)}
                      className="text-sm font-bold text-slate-500 hover:text-slate-900"
                    >
                      Volver al inicio de sesión
                    </Button>
                  )}
                </div>

                {isDebugMode && (
                  <div className="pt-4 border-t border-slate-100 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTestSentry}
                      className="w-full h-12 border-orange-200 text-orange-600 hover:bg-orange-50 font-bold rounded-xl"
                    >
                      <Bug className="mr-2 h-4 w-4" />
                      DEBUG SENTRY
                    </Button>
                    <p className="text-[10px] text-center text-slate-400 mt-2 uppercase font-bold tracking-widest">
                      Modo Debug Activo
                    </p>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
